import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiGithub, FiTwitter, FiLinkedin, FiMail, FiMapPin, FiPhone } = FiIcons;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();
  const copyrightTapRef = useRef({ count: 0, lastTap: 0 });

  const goToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');

      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }, 250);
    } else {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleCopyrightTap = () => {
    const now = Date.now();
    const previous = copyrightTapRef.current;
    const count = now - previous.lastTap <= 1200 ? previous.count + 1 : 1;

    copyrightTapRef.current = { count, lastTap: now };

    if (count >= 3) {
      copyrightTapRef.current = { count: 0, lastTap: 0 };
      navigate('/admin/login');
    }
  };

  const footerLinks = [
    {
      title: 'Υπηρεσίες',
      links: [
        { name: 'Web Development', sectionId: 'services' },
        { name: 'Mobile Apps', sectionId: 'services' },
        { name: 'UI/UX Design', sectionId: 'services' },
        { name: 'Cloud Solutions', sectionId: 'services' },
      ],
    },
    {
      title: 'Εταιρεία',
      links: [
        { name: 'Σχετικά', sectionId: 'about' },
        { name: 'Έργα', sectionId: 'projects' },
        { name: 'Επικοινωνία', sectionId: 'contact' },
        { name: 'DevVault Privacy', to: '/devvault-privacy', isInternal: true },
      ],
    },
  ];

  return (
    <footer className="bg-slate-900 text-slate-300 pt-20 pb-10">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div className="space-y-6">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-xl">C</span>
              </div>
              <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
                CodeVaro
              </span>
            </Link>

            <p className="text-slate-400 leading-relaxed">
              Μετατρέπουμε τις ιδέες σας σε προηγμένες ψηφιακές λύσεις. Εξειδίκευση στο Web Development και τον ψηφιακό μετασχηματισμό.
            </p>

            <div className="flex space-x-4">
              {[FiGithub, FiTwitter, FiLinkedin].map((Icon, index) => (
                <motion.a
                  key={index}
                  href="#"
                  whileHover={{ y: -3 }}
                  className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center transition-colors"
                >
                  <SafeIcon icon={Icon} className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>

          {footerLinks.map((section, index) => (
            <div key={index}>
              <h4 className="text-white font-bold text-lg mb-6">{section.title}</h4>
              <ul className="space-y-4">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    {link.isInternal ? (
                      <Link
                        to={link.to}
                        className="hover:text-blue-500 transition-colors"
                      >
                        {link.name}
                      </Link>
                    ) : (
                      <button
                        onClick={() => goToSection(link.sectionId)}
                        className="hover:text-blue-500 transition-colors text-left"
                      >
                        {link.name}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h4 className="text-white font-bold text-lg mb-6">Επικοινωνία</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <SafeIcon icon={FiMapPin} className="w-5 h-5 text-blue-500 mt-1" />
                <span>Αθήνα, Ελλάδα</span>
              </li>
              <li className="flex items-center space-x-3">
                <SafeIcon icon={FiPhone} className="w-5 h-5 text-blue-500" />
                <span>+30 210 0000000</span>
              </li>
              <li className="flex items-center space-x-3">
                <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-500" />
                <span>info@codevaro.eu</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
          <p
            onClick={handleCopyrightTap}
            className="inline-block select-none cursor-default"
            title=""
          >
            © {currentYear} CodeVaro. Όλα τα δικαιώματα διατηρούνται.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;